import { useState, useCallback, useEffect } from 'react'
import { apiClient } from '@/lib/api'
import type { NotificationListData } from '@/types/notifications'

export function useNotifications() {
  const [data, setData] = useState<NotificationListData>({ notifications: [], unread_count: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true)
      const res = await apiClient.get<NotificationListData>('/api/v1/tenants/notifications')
      setData(res)
      setError(null)
    } catch (err: any) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  const markRead = async (ids: string[]) => {
    try {
      await apiClient.post('/api/v1/tenants/notifications/mark-read', { notification_ids: ids })
      // Optimistic update
      setData(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => 
          ids.includes(n.id) ? { ...n, is_read: true } : n
        ),
        unread_count: Math.max(0, prev.unread_count - ids.length)
      }))
    } catch (err: any) {
      console.error('Failed to mark notifications read', err)
      fetchNotifications() // Revert on failure
    }
  }

  const markAllRead = async () => {
    try {
      await apiClient.post('/api/v1/tenants/notifications/mark-all-read')
      // Optimistic update
      setData(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, is_read: true })),
        unread_count: 0
      }))
    } catch (err: any) {
      console.error('Failed to mark all read', err)
      fetchNotifications()
    }
  }

  // Initial fetch
  useEffect(() => {
    fetchNotifications()
    // Poll every 2 minutes
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  return {
    notifications: data.notifications,
    unreadCount: data.unread_count,
    loading,
    error,
    fetchNotifications,
    markRead,
    markAllRead
  }
}
